import type { AuthResponse, LoginCredentials, RegisterData, User, UserPermissionsResponse } from 'shared/types';
import api from '../axios';
 

// Simplified Auth Service Class
export class AuthService {
  private readonly TOKEN_KEY = 'accessToken';
  private readonly USER_KEY = 'user';

  // Simple token validation - just check if it exists and is properly formatted
  private isTokenValid(token: string): boolean {
    try {
      // Basic JWT format check
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      
      // Try to decode payload to check expiration
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch {
      return false;
    }
  }

  // Secure token storage
  private setAuthData(authResponse: AuthResponse): void {
    try {
      localStorage.setItem(this.TOKEN_KEY, authResponse?.access_token || '');
      localStorage.setItem(this.USER_KEY, JSON.stringify(authResponse.user));
    } catch (error) {
      console.error('Failed to store auth data:', error);
      throw new Error('Failed to store authentication data');
    }
  }

  // Authentication Methods
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    
    if (response?.data?.access_token && response?.data?.user) {
      this.setAuthData(response.data);
    } else {
      throw new Error('Invalid response format from server');
    }
    
    return response.data;
  }

  async register(data: RegisterData): Promise<User> {
    const response = await api.post<User>('/auth/register', data);
    return response.data;
  }

  async getProfile(): Promise<User> {
    const response = await api.get<User>('/auth/profile');
    
    // Update local storage with fresh user data
    if (response.data) {
      localStorage.setItem(this.USER_KEY, JSON.stringify(response.data));
    }
    
    return response.data;
  }

  async changePassword(newPassword: string): Promise<{ success: boolean }> {
    const response = await api.post<{ success: boolean }>('/auth/change-password', { 
      newPassword 
    });
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      // Call backend logout if available (uncomment when endpoint exists)
      // await api.post('/auth/logout');
    } catch (error) {
      console.warn('Backend logout failed:', error);
    } finally {
      // Always clear local storage
      this.clearAuthData();
    }
  }

  private clearAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  // User Management (Admin only)
  async deactivateUser(userId: string): Promise<{ success: boolean }> {
    const response = await api.post<{ success: boolean }>('/auth/deactivate-user', { 
      userId 
    });
    return response.data;
  }

  // Permission Management
  async getUserPermissions(userId?: string): Promise<UserPermissionsResponse> {
    const endpoint = userId ? `/auth/permissions/user/${userId}` : '/auth/permissions/me';
    const response = await api.get<UserPermissionsResponse>(endpoint);
    return response.data;
  }

  async getMyPermissions(): Promise<UserPermissionsResponse> {
    return this.getUserPermissions();
  }

  // Utility Methods
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    return !!(token && this.isTokenValid(token));
  }

  getCurrentUser(): User | null {
    try {
      const userStr = localStorage.getItem(this.USER_KEY);
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Failed to parse user data:', error);
      return null;
    }
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user?.role === role;
  }

  isSuperAdmin(): boolean {
    return this.hasRole('SUPER_ADMIN');
  }

  isAdmin(): boolean {
    return this.hasRole('ADMIN') || this.isSuperAdmin();
  }

  isUser(): boolean {
    return this.hasRole('USER');
  }
}

// Create and export singleton instance
export const authService = new AuthService();

// Export individual methods for convenience
export const {
  login,
  register,
  getProfile,
  changePassword,
  logout,
  deactivateUser,
  getUserPermissions,
  getMyPermissions,
  isAuthenticated,
  getCurrentUser,
  getAccessToken,
  hasRole,
  isSuperAdmin,
  isAdmin,
  isUser
} = authService;